import React from 'react';
import Navbar from '../layout/Navbar';
import Footer from '../layout/Footer';
import { ChevronRight, Shield, Smartphone, Lock, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const AppPrivacyPolicy = () => {
    const sections = [
        {
            title: "1. Information We Collect",
            subsections: [
                {
                    subtitle: "Personal Information:",
                    items: [
                        "Name, email address, phone number, and other profile details you provide when registering or updating your account",
                    ]
                },
                {
                    subtitle: "Health Information:",
                    items: [
                        "Consultation records, lab and radiology reports, prescriptions, and other health-related data you upload or store in the App",
                    ]
                },
                {
                    subtitle: "Device and Usage Data:",
                    items: [
                        "Device model, operating system version, app usage logs, crash reports (collected via libraries such as device_info_plus)",
                    ]
                },
                {
                    subtitle: "Local Storage and Cache:",
                    items: [
                        "Settings or documents stored locally using shared_preferences and file storage (path_provider)",
                    ]
                }
            ]
        },
        {
            title: "2. Payment Feature",
            isImportant: true,
            content: "The App currently does NOT have the payment feature active. Although the project includes the razorpay_flutter package as a dependency, payment processing is not enabled in the live App. If payments are activated in the future, we will update this policy and clearly note which payment provider is used and how payment data is handled."
        },
        {
            title: "3. How We Use Information",
            subsections: [
                {
                    subtitle: "We use the information we collect to:",
                    items: [
                        "Provide and improve App features (consultations, viewing reports, generating receipts/invoices)",
                        "Store and display medical records you upload",
                        "Investigate and fix bugs, and to monitor performance and stability",
                        "Comply with legal obligations and protect the safety of users"
                    ]
                }
            ]
        },
        {
            title: "4. Sharing and Disclosure",
            subsections: [
                {
                    subtitle: "Third-Party Service Providers:",
                    items: [
                        "When features require external services (for example printing libraries or analytics), we may share the minimum information needed with those service providers",
                        "Currently payment providers are not used because payments are inactive"
                    ]
                },
                {
                    subtitle: "With Your Consent:",
                    items: [
                        "If you explicitly choose to share a report or record with a doctor or lab, that data will be shared with the recipient you select"
                    ]
                },
                {
                    subtitle: "Legal Requirements:",
                    items: [
                        "We may disclose information if required by law or to protect rights, property, or safety"
                    ]
                }
            ]
        },
        {
            title: "5. Sensitive Health Data",
            content: "Health information is treated as sensitive. We use reasonable technical and organizational safeguards to protect such data. Depending on your jurisdiction, additional legal protections may apply (for example, HIPAA, GDPR). If you require specific compliance assurances, please contact us.",
            icon: Shield
        },
        {
            title: "6. Device Permissions",
            content: "The App may request permissions (storage, camera, microphone) only when necessary for a feature. You can disable permissions in your device settings; disabling required permissions may break related features.",
            icon: Smartphone
        },
        {
            title: "7. Data Retention and Deletion",
            content: "We retain personal and health data only as long as necessary to provide the service or as required by law. You may request access to, correction of, or deletion of your personal data by contacting us."
        },
        {
            title: "8. Security",
            content: "We take reasonable measures to protect information (secure transmission and storage). No system is completely secure; in the event of a breach, we will follow applicable notification requirements.",
            icon: Lock
        },
        {
            title: "9. Children's Privacy",
            content: "The App is intended for adult users. If you are a parent or guardian and believe your child has provided personal information, contact us to request removal."
        },
        {
            title: "10. Changes to This Policy",
            content: "We may update this policy. If we make material changes (for example enabling payments), we will post the updated policy with a new effective date."
        },
        {
            title: "11. Contact",
            content: "For questions, data access, or deletion requests, contact us at:"
        }
    ];

    return (
        <>
            <Navbar />

            {/* Hero Section */}
            <div className="relative w-full h-64 bg-gradient-to-r from-[#009CA6] to-[#00868C] flex items-center">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full -mr-48 -mt-48"></div>
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-white rounded-full -ml-32 -mb-32"></div>
                </div>
                <div className="container mx-auto px-4 relative z-10">
                    <div>
                        <div className="flex items-center gap-2 mb-3">
                            <Smartphone className="w-8 h-8 text-white" />
                            <div className="w-12 h-1 bg-[#AD3DC5]"></div>
                        </div>
                        <h1 className="text-4xl font-bold text-white">Mobile App Privacy Policy</h1>
                        <p className="text-teal-100 mt-2">tcbhu Mobile Application - Your privacy is our priority</p>
                    </div>
                </div>
            </div>

            {/* Breadcrumb */}
            <div className="bg-gray-50 border-b border-gray-200">
                <div className="container mx-auto px-4 py-4">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Link to="/" className="text-[#009CA6] hover:text-[#00868C]">Home</Link>
                        <ChevronRight className="w-4 h-4" />
                        <span className="text-gray-800 font-medium">App Privacy Policy</span>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="bg-white py-12">
                <div className="container mx-auto px-4 max-w-4xl">
                    {/* Effective Date */}
                    <div className="mb-8 p-4 bg-blue-50 border-l-4 border-[#009CA6] rounded">
                        <p className="text-sm text-gray-700">
                            <strong>Effective Date:</strong> December 17, 2025
                        </p>
                        <p className="text-sm text-gray-600 mt-1">
                            This Privacy Policy describes how the <strong>tcbhu</strong> mobile application collects, uses, shares, and protects information. Please read this policy carefully.
                        </p>
                    </div>

                    {/* Content Sections */}
                    <div className="space-y-8">
                        {sections.map((section, index) => (
                            <div key={index} className="pb-8 border-b border-gray-200 last:border-b-0">
                                {/* Section Title */}
                                <h2 className="text-2xl font-bold text-[#1B3C74] mb-4 flex items-center gap-3">
                                    {section.icon && <section.icon className="w-6 h-6 text-[#009CA6]" />}
                                    <span className="text-[#009CA6]">{section.title.split('.')[0]}.</span>
                                    <span>{section.title.split('. ')[1]}</span>
                                </h2>

                                {/* Important Notice Box */}
                                {section.isImportant && section.content && (
                                    <div className="mb-4 p-6 bg-amber-50 border-l-4 border-amber-500 rounded-lg">
                                        <div className="flex items-start gap-3">
                                            <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                                            <div>
                                                <h3 className="font-bold text-amber-800 mb-2">IMPORTANT</h3>
                                                <p className="text-gray-700 leading-relaxed">
                                                    {section.content}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Simple Content */}
                                {section.content && !section.isImportant && (
                                    <p className="text-gray-700 leading-relaxed mb-4">
                                        {section.content}
                                    </p>
                                )}

                                {/* Subsections */}
                                {section.subsections && (
                                    <div className="space-y-4">
                                        {section.subsections.map((subsection, subIndex) => (
                                            <div key={subIndex}>
                                                <h3 className="font-semibold text-gray-800 mb-3">
                                                    {subsection.subtitle}
                                                </h3>
                                                <ul className="space-y-2 pl-6">
                                                    {subsection.items.map((item, itemIndex) => (
                                                        <li
                                                            key={itemIndex}
                                                            className="text-gray-700 flex items-start gap-3"
                                                        >
                                                            <span className="text-[#009CA6] font-bold mt-0.5">•</span>
                                                            <span>{item}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Contact Section - Special Handling */}
                                {index === sections.length - 1 && (
                                    <div className="mt-6 p-6 bg-gradient-to-br from-gray-50 to-teal-50 rounded-lg border border-gray-200">
                                        <div className="space-y-3">
                                            <div>
                                                <p className="font-semibold text-gray-800 text-lg">Trauma Centre BHU</p>
                                                <p className="text-gray-600 text-sm">tcbhu Mobile Application Support</p>
                                            </div>
                                            <div>
                                                <p className="text-sm text-gray-600">
                                                    <strong>Email:</strong>{' '}
                                                    <a href="mailto:privacy@traumacentrebhu.com" className="text-[#009CA6] hover:text-[#00868C] font-medium">
                                                        privacy@traumacentrebhu.com
                                                    </a>
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Key Highlights */}
                    <div className="mt-12 grid md:grid-cols-3 gap-6">
                        <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                            <Shield className="w-10 h-10 text-[#009CA6] mb-3" />
                            <h3 className="font-bold text-gray-800 mb-2">Secure Data</h3>
                            <p className="text-sm text-gray-600">
                                Your health information is protected with industry-standard security measures
                            </p>
                        </div>
                        <div className="bg-purple-50 p-6 rounded-lg border border-purple-200">
                            <Lock className="w-10 h-10 text-[#AD3DC5] mb-3" />
                            <h3 className="font-bold text-gray-800 mb-2">No Active Payments</h3>
                            <p className="text-sm text-gray-600">
                                Payment features are currently disabled - your financial data is not collected
                            </p>
                        </div>
                        <div className="bg-teal-50 p-6 rounded-lg border border-teal-200">
                            <Smartphone className="w-10 h-10 text-[#009CA6] mb-3" />
                            <h3 className="font-bold text-gray-800 mb-2">Control Permissions</h3>
                            <p className="text-sm text-gray-600">
                                Manage app permissions through your device settings at any time
                            </p>
                        </div>
                    </div>

                    {/* Important Notice */}
                    <div className="mt-12 p-6 bg-amber-50 border-l-4 border-amber-500 rounded">
                        <h3 className="font-bold text-gray-800 mb-2">Important Notice</h3>
                        <p className="text-sm text-gray-700">
                            This Privacy Policy complies with applicable data protection regulations. Depending on your jurisdiction, additional legal protections may apply (for example, HIPAA, GDPR). If you have any concerns about our privacy practices or believe your rights have been violated, please contact us immediately.
                        </p>
                    </div>

                    {/* CTA Section */}
                    <div className="mt-12 bg-gradient-to-r from-[#009CA6] to-[#00868C] rounded-lg p-8 text-center">
                        <Smartphone className="w-16 h-16 text-white mx-auto mb-4" />
                        <h3 className="text-2xl font-bold text-white mb-3">Questions About App Privacy?</h3>
                        <p className="text-teal-100 mb-6">
                            Our dedicated support team is ready to help you with any privacy-related concerns about the mobile app.
                        </p>
                        <Link
                            to="/contact-us"
                            className="inline-block bg-white text-[#009CA6] font-semibold px-8 py-3 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                            Contact Support
                        </Link>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    );
};

export default AppPrivacyPolicy;
